import React, { useEffect, useState } from "react";
import { Upload, Button, Modal, message } from "antd";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import type { RcFile } from "rc-upload/lib/interface";
import { UploadOutlined, EyeOutlined, DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import { useFileSave, useFileDelete } from "../../hooks/api/file/hooks";
import { toBase64 } from "../../lib/utils";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMessageApi } from "../../provider/AntdNotification";

interface UploadImageProps {
  value?: string[] | string;
  uploadButton?: React.ReactNode;
  count?: number;
  cropImage?: {
    aspect?: number;
    shape?: "rect" | "round";
    quality?: number;
  } | null;
  cropShape?: "rect" | "round";
  disabled?: boolean;
  classs?: string;
  onChange?: (urls: string[] | string) => void;
  listType?: 'text' | 'picture' | 'picture-card';
  canReorder?: boolean; // new prop
  isFavIcon?: boolean;
  acceptType?: "image" | "video" | "any";
  accept?: string;
}

// Sortable Item wrapper
function SortableUploadListItem({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const modifiedChildren = React.cloneElement(children as React.ReactElement, {
    onMouseDown: (e: any) => {
      if (
        e.target.closest(".ant-upload-list-item-actions") ||
        e.target.closest(".ant-upload-list-item-card-actions") ||
        e.target.closest("[role='button']") ||
        e.target.closest("button")
      ) {
        e.stopPropagation();
      }
    },
    onClick: (e: any) => {
      if (
        e.target.closest(".ant-upload-list-item-actions") ||
        e.target.closest(".ant-upload-list-item-card-actions") ||
        e.target.closest("[role='button']") ||
        e.target.closest("button")
      ) {
        e.stopPropagation();
      }
    },
  });

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {modifiedChildren}
    </div>
  );
}

const UploadImage: React.FC<UploadImageProps> = ({
  value = [],
  uploadButton = <Button className="upload-button" icon={<UploadOutlined />} />,
  count = 1,
  cropImage = null,
  cropShape = "rect",
  disabled = false,
  classs,
  onChange,
  listType = "picture",
  canReorder = false, // default false
  isFavIcon = false,
  acceptType = "image",
  accept,
}) => {
  const messageNotifier = useMessageApi();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const { mutate: save } = useFileSave({
    onSuccess: (res) => {
      setFileList((prev) => {
        const filtered = prev.filter((f) => f.status !== "uploading");
        const newFile: UploadFile = {
          uid: res.data._id,
          name: res.data.name,
          url: res.data.url,
          status: "done",
        };
        const newList = [...filtered, newFile];
        triggerChange(newList);
        return newList;
      });
      messageNotifier.success("Uploaded successfully");
    },
    onError: () => {
      setFileList((prev) => prev.filter((f) => f.status !== "uploading"));
      messageNotifier.error("Upload failed");
    },
  });

  const { mutate: remove } = useFileDelete({
    onSuccess: () => messageNotifier.success("Deleted successfully"),
    onError: () => messageNotifier.error("Delete failed"),
  });

  const triggerChange = (files: UploadFile[]) => {
    const urls = files.map((x) => x.url as string).filter(Boolean);
    if (count === 1) onChange?.(urls[0] || "");
    else onChange?.(urls);
  };

  // Initialize fileList from value
  useEffect(() => {
    const valArray = Array.isArray(value) ? value : value ? [value] : [];
    const urlsInState = fileList.map((f) => f.url).filter(Boolean);
    if (JSON.stringify(urlsInState) !== JSON.stringify(valArray)) {
      const initialFiles = valArray.map((url, index) => ({
        uid: `init-${index}`,
        name: `Image-${index + 1}`,
        status: "done" as UploadFile["status"],
        url,
      }));
      setFileList(initialFiles);
    }
  }, [value]);

  const beforeUpload = async (file: RcFile) => {
    const isVideo = acceptType === "video" || file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    const isValid = acceptType === "any" || isVideo || isImage;
    const maxMb = isVideo ? 200 : 10;
    const isLtMax = file.size / 1024 / 1024 < maxMb;

    if (!isValid) messageNotifier.error("Invalid file type!");
    if (!isLtMax) messageNotifier.error(`File must be smaller than ${maxMb}MB!`);
    if (!isValid || !isLtMax) return Upload.LIST_IGNORE;

    // Add temporary loading placeholder file
    const tempUid = `temp-${Date.now()}`;
    setFileList((prev) => [
      ...prev,
      {
        uid: tempUid,
        name: file.name,
        status: "uploading",
        percent: 50,
      },
    ]);

    const formData = new FormData();
    formData.append("file", file);
    save(formData);
    return false; // prevent antd auto-upload
  };

  const handleRemove = (file: UploadFile) => {
    if (file.url) remove({ url: file.url });
    setFileList((prev) => {
      const updated = prev.filter((f) => f.uid !== file.uid);
      triggerChange(updated);
      return updated;
    });
  };

  // Peter, Oct 2026: the bin icon sat directly on the thumbnail, right
  // next to the preview/enlarge icon in the same hover overlay, and a
  // misclick deleted the image immediately with no way back ("disappear
  // for good"). Confirm first, every time.
  const confirmRemove = (file: UploadFile) => {
    Modal.confirm({
      title: "Delete this image?",
      content: "This cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => handleRemove(file),
    });
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview && file.originFileObj) {
      file.preview = await toBase64(file.originFileObj);
    }
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(file.name || file.url?.split("/").pop() || "");
  };

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fileList.findIndex((f) => f.uid === active.id);
    const newIndex = fileList.findIndex((f) => f.uid === over.id);
    const newList = arrayMove(fileList, oldIndex, newIndex);
    setFileList(newList);
    triggerChange(newList);
  };

  // Peter, Oct 2026: "can the bin be moved outside the photo?" - for
  // picture-card, the actions (preview/delete) now render in their own
  // row below the thumbnail instead of as a hover overlay on top of it,
  // so there's no overlap between enlarging and deleting an image.
  const renderCardItem = (file: UploadFile) => {
    const isUploading = file.status === "uploading";
    return (
      <div className="flex flex-col items-center gap-1 w-full">
        <div className="w-full aspect-square rounded-md overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center">
          {isUploading ? (
            <LoadingOutlined style={{ fontSize: 20, color: "#999" }} />
          ) : (
            <img
              src={file.url || (file.preview as string)}
              alt={file.name}
              className="w-full h-full object-cover"
            />
          )}
        </div>
        {!isUploading && (
          <div className="flex items-center justify-center gap-2">
            <Button
              size="small"
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(file)}
            />
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => confirmRemove(file)}
            />
          </div>
        )}
      </div>
    );
  };

  const itemRender = (originNode: React.ReactNode, file: UploadFile, currFileList: UploadFile[]) => {
    const node = listType === "picture-card" ? renderCardItem(file) : originNode;
    if (canReorder && currFileList.length > 1) {
      return <SortableUploadListItem id={file.uid}>{node}</SortableUploadListItem>;
    }
    return node;
  };

  const uploadProps: UploadProps = {
    accept: accept || (acceptType === "video" ? "video/*" : "image/*"),
    fileList,
    onRemove: handleRemove,
    onPreview: handlePreview,
    beforeUpload,
    listType,
    disabled,
    maxCount: count,
    // maxCount alone only caps the total across repeated uploads - without
    // `multiple`, the native file dialog itself only ever allows picking
    // one file per click, so a field meant for several images (e.g. Art
    // Gallery Images, count=10) still forced one-at-a-time uploads
    // (Oliver's testing doc, round 6). Fields genuinely meant for a single
    // image (count=1, e.g. the one featured Artwork Image) correctly stay
    // single-select.
    multiple: count > 1,
    className: classs,
    itemRender: (listType === "picture-card" || canReorder) ? itemRender : undefined,
  };
  const uploadComponent = cropImage ? (
    <ImgCrop
      {...cropImage}
    >
      <Upload {...uploadProps}>{fileList.length >= count ? null : uploadButton}</Upload>
    </ImgCrop>
  ) : (
    <Upload {...uploadProps}>{fileList.length >= count ? null : uploadButton}</Upload>
  );

  const content = (
    <>
      <div className={listType === "picture-card" ? "arr-upload-card-fix" : undefined}>
        {listType === "picture-card" && (
          // Peter, Oct 2026: moving preview/delete below the thumbnail made
          // each item taller than AntD's fixed-height picture-card box, so
          // the extra row spilled out and overlapped whatever rendered
          // underneath (the Alt Text labels/inputs). The box now grows to
          // fit its content instead of clipping/overflowing it.
          <style>{`
            .arr-upload-card-fix .ant-upload-list-picture-card .ant-upload-list-item-container,
            .arr-upload-card-fix .ant-upload-list-item-container {
              height: auto !important;
              margin-bottom: 8px;
            }
            .arr-upload-card-fix .ant-upload-list-item {
              height: auto !important;
              position: static !important;
              padding: 8px !important;
            }
            .arr-upload-card-fix .ant-upload-list-item::before {
              display: none !important;
            }
          `}</style>
        )}
        {canReorder && fileList.length > 1 ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={fileList.map((f) => f.uid)} strategy={verticalListSortingStrategy}>
              {uploadComponent}
            </SortableContext>
          </DndContext>
        ) : (
          uploadComponent
        )}
      </div>
      <Modal open={previewOpen} title={previewTitle} footer={null} onCancel={() => setPreviewOpen(false)}>
        <img alt="preview" style={{ width: "100%" }} src={previewImage || "/placeholder.svg"} />
      </Modal>
    </>
  );

  return content;
};

export default UploadImage;
